import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { ajax, AjaxRequest } from 'rxjs/ajax';
import { ApiResult } from '../entities';
import { map, catchError, tap, filter, retry, take, concatMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { OAuthToken } from '../entities';
import { setBearerToken } from './local-storage.api';
import { ResponseError } from '../../utils';

const cachedRequestsMap = new Map<string, { timeout: number, response: Observable<any> }>();

type token = 'waiting' | 'failed' | OAuthToken;
let token: token;
try {
  token = JSON.parse(localStorage.getItem('oauthToken')) ?? 'waiting';
} catch (err) {
  token = 'waiting';
}

const tokenSubject = new BehaviorSubject<token>(token);

export const buildUrl = (path: string): string =>
  (path.startsWith('/'))
    ? `${environment.remoteApi}${environment.apiPath}${path}`
    : `${environment.remoteApi}${environment.apiPath}/${path}`;

export let LPPlayerId: string | undefined;

export const xhrApi = {
  get<T>(path: string, maxAge: number = 0): Observable<T> {
    if (maxAge && !cachedRequestsMap.has(path)) {
      const response = ajaxRequestWrapper<T>('GET', environment.apiVersion + path);
      const timeout = new Date().getTime() + maxAge;
      cachedRequestsMap.set(path, { response, timeout });
    }
    const cache = cachedRequestsMap.get(path);
    return cache && cache.timeout > new Date().getTime() ? cache.response : ajaxRequestWrapper<T>('GET', environment.apiVersion + path)
      .pipe(
        map((response) => {
          const apiResult: ApiResult = response;
          return apiResult.datas ? apiResult.datas : apiResult;
        }),
      );
  },

  post<T>(path: string, body: any): Observable<T> {
    return ajaxRequestWrapper<T>('POST', environment.apiVersion + path, JSON.stringify(body));
  },

  put<T>(path: string, body: any): Observable<T> {
    return ajaxRequestWrapper<T>('PUT', environment.apiVersion + path, JSON.stringify(body));
  },

  patch<T>(path: string, body: any): Observable<T> {
    return ajaxRequestWrapper<T>('PATCH', environment.apiVersion + path, JSON.stringify(body));
  },

  delete<T>(path: string): Observable<T> {
    return ajaxRequestWrapper<T>('DELETE', environment.apiVersion + path);
  },

  auth(email: string, password: string, rememberUser: boolean): Observable<OAuthToken> {
    if (environment.enableDesktopMode) {
      return new Observable((resolve) => {
        setBearerToken(JSON.parse('{"access_token":"desktop"}'));
      });
    }
    const body = encodeURI(
      `grant_type=password&client_id=lp-remote-client&client_secret=lp-remote-secret&username=${email}&password=${password}`
    );
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    return ajax(makeRequest('POST', 'oauth/token', body, headers, true))
    .pipe(
      map((response) => {
        const apiResult: ApiResult = response.response;
        return apiResult.datas || apiResult;
      }),
      tap((oauthToken: OAuthToken) => {
        if (rememberUser) {
          setBearerToken(oauthToken);
        }
        tokenSubject.next(oauthToken);
      }));
  },

  refresh() {
    if (environment.enableDesktopMode) {
      return new Observable((resolve) => {
        setBearerToken(JSON.parse('{"access_token":"desktop"}'));
      });
    }

    let refresh_token;
    try {
      refresh_token = JSON.parse(localStorage.getItem('oauthToken')).refresh_token;
    } catch (err) {
      return throwError(new ResponseError('Could not retrieve oauth token from localStorage', 401));
    }
    const body = encodeURI(
      `grant_type=refresh_token&client_id=lp-remote-client&client_secret=lp-remote-secret&refresh_token=${refresh_token}`);
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    return ajax.post(buildUrl('oauth/token'), body, headers).pipe(
      map(response => {
        const oauthToken = response.response;
        setBearerToken(oauthToken);
        return oauthToken;
      }),
      catchError(_ => throwError(new ResponseError('Could not retrieve oauth token from server', 401)))
    );
  },
  setPlayerId(playerId: string): void {
    LPPlayerId = playerId;
  }
};

tokenSubject.pipe(filter(oauthToken => oauthToken === 'waiting')).subscribe(_ => {
  xhrApi.refresh().subscribe(
    oauthToken => tokenSubject.next(oauthToken),
    _err => tokenSubject.next('failed')
  );
});

function makeRequest(method: string, path: string, body?: string, headers = {}, auth = false) {
  const url = buildUrl(path);
  const req: AjaxRequest = {
    async: true,
    crossDomain: true,
    responseType: 'json',
    timeout: 0,
    withCredentials: false,
    url, method,
    headers: LPPlayerId && !auth ?
      {
        LPPlayerId,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...headers
      } :
      {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers
    }
  };
  if (body) {
    req.body = body;
  }
  return req;
}

function ajaxRequestWrapper<T>(method: string, path: string, body?: string, headers = {}): Observable<T | never> {
  return tokenSubject.pipe(filter((oauthToken) => oauthToken !== 'waiting')).pipe(
    take(1),
    concatMap(oauthToken => {
      if (oauthToken === 'failed') {
        return throwError(new ResponseError('Failed to retrieve token', 401));
      }
      const accessToken = (oauthToken as OAuthToken).access_token;
      const authorization = accessToken && `Bearer ${accessToken}`;
      const req = makeRequest(method, path, body, { ...headers, Authorization: authorization });
      return ajax(req);
    }),
    map(response => [, response]),
    catchError(responseError => {
      if (responseError.status === 401) {
        const oauthToken = tokenSubject.getValue();
        if (responseError.response?.error === 'invalid_token' && oauthToken !== 'waiting' && oauthToken !== 'failed') {
          tokenSubject.next('waiting');
          return throwError(new ResponseError(responseError.message, responseError.status));
        }
      }
      return [[responseError, ]];
    })
    ).pipe(
      retry(1),
      map(([responseError, response]) => {
        if (responseError !== undefined) {
          throw responseError;
        }
        const apiResult: ApiResult = response.response;
        return apiResult.datas || apiResult;
      })
  );
}
